// Content-engine API client (apps/app). Mirrors lib/family.ts.
// docs/replan-v2.md R5.

import type { content } from '@fajrarabi/zod-schemas';

import { API_BASE, api } from './api';
import { enqueue } from './offline-queue';

// A fetch failure (no response) means we're offline; a thrown ApiError (the
// server answered) is a real error and must propagate.
function isOffline(err: unknown): boolean {
  return err instanceof TypeError || (typeof navigator !== 'undefined' && !navigator.onLine);
}

const STAGING_CDN = 'https://staging-cdn.fajrarabi.com';
const PROD_CDN = 'https://cdn.fajrarabi.com';

// CDN base for resolving relative media keys in activity payloads. Mirrors the
// host-based API_BASE logic.
export function cdnBase(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.fajrarabi.com') return PROD_CDN;
    if (host === 'staging-app.fajrarabi.com') return STAGING_CDN;
  }
  return (import.meta.env.VITE_CDN_BASE as string | undefined) ?? STAGING_CDN;
}

export type LessonDto = content.LessonDto;
export type AttemptInput = content.AttemptInput;
export type AttemptResultDto = content.AttemptResultDto;
export type LessonCompleteResultDto = content.LessonCompleteResultDto;

export interface TodayResponse {
  lesson: LessonDto | null;
  status?: 'in_progress' | 'not_started';
  // True when the next lesson is behind the paywall (paid unit, no entitling
  // subscription). The home shows an upgrade card instead of "continue".
  locked?: boolean;
  me: { streak: number; xp: number };
}

export async function getToday(signal?: AbortSignal): Promise<TodayResponse> {
  return api<TodayResponse>('/kids/today', { signal });
}

export async function getLesson(id: string, signal?: AbortSignal): Promise<LessonDto> {
  const { lesson } = await api<{ lesson: LessonDto }>(`/kids/lessons/${id}`, { signal });
  return lesson;
}

export async function submitAttempt(
  activityId: string,
  attempt: AttemptInput,
): Promise<AttemptResultDto> {
  try {
    const { result } = await api<{ result: AttemptResultDto }>(
      `/kids/activities/${activityId}/attempt`,
      { method: 'POST', body: attempt },
    );
    return result;
  } catch (err) {
    if (!isOffline(err)) throw err;
    // Offline: persist the raw attempt for replay, and acknowledge optimistically.
    // The real (server-authoritative) score lands on the next sync + refetch.
    await enqueue({
      url: `${API_BASE}/kids/activities/${activityId}/attempt`,
      method: 'POST',
      body: attempt,
      kind: 'attempt',
      queuedAt: Date.now(),
    });
    return { activityId, score: 0, correct: true, passed: true, xpAwarded: 0 };
  }
}

export async function completeLesson(lessonId: string): Promise<LessonCompleteResultDto> {
  try {
    const { result } = await api<{ result: LessonCompleteResultDto }>(
      `/kids/lessons/${lessonId}/complete`,
      { method: 'POST' },
    );
    return result;
  } catch (err) {
    if (!isOffline(err)) throw err;
    await enqueue({
      url: `${API_BASE}/kids/lessons/${lessonId}/complete`,
      method: 'POST',
      body: {},
      kind: 'complete',
      queuedAt: Date.now(),
    });
    // Offline: the real result (incl. any badges) lands when the queue flushes.
    return {
      lessonId,
      score: 0,
      xpAwarded: 0,
      streak: { current: 0, longest: 0 },
      badgesEarned: [],
    };
  }
}

// ─── Badges (achievements wall) ─────────────────────────────────────────────

export type BadgeDto = content.BadgeDto;

export async function listBadges(signal?: AbortSignal): Promise<BadgeDto[]> {
  const { badges } = await api<{ badges: BadgeDto[] }>('/kids/badges', { signal });
  return badges;
}

// ─── Certificates (one per fully-completed unit) ────────────────────────────

export interface CertificateDto {
  unitId: string;
  unitTitleKey: string;
  trackTitleKey: string;
  subjectSlug: string;
  lessonCount: number;
  completedAt: string | null;
}

export async function listCertificates(signal?: AbortSignal): Promise<CertificateDto[]> {
  const { certificates } = await api<{ certificates: CertificateDto[] }>('/kids/certificates', {
    signal,
  });
  return certificates;
}

// ─── Parent progress dashboard ──────────────────────────────────────────────

export interface KidProgress {
  timeSpentMs: { week: number; month: number; allTime: number };
  lastActivityAt: string | null;
  lastLoginAt: string | null;
  streak: { current: number; longest: number; freezeUsedThisWeek: boolean };
  xp: { total: number; thisWeek: number; weekly: number[] };
  lessons: { completed: number; inProgress: number; trackTotal: number; trackPercent: number };
  heatmap: Array<{ date: string; count: number }>;
  subjects: Array<{ slug: string; completed: number }>;
  topActivityTypes: Array<{ type: string; count: number }>;
}

export async function getKidProgress(kidId: string, signal?: AbortSignal): Promise<KidProgress> {
  const { progress } = await api<{ progress: KidProgress }>(`/parent/kids/${kidId}/progress`, {
    signal,
  });
  return progress;
}
